import { IFeatureMenuQuery } from 'generated/sanity-graphql';

import { useMenuExperimentVariant } from './use-menu-experiment-variant-name';

interface IMenuVariant {
  exposureFlagKey: string;
  exposurePathname?: string;
  isPending?: false;
  isVariantSelected: boolean;
  variantName?: string;
  menuVariantId?: string;
}

interface IPendingMenuVariant {
  exposureFlagKey?: undefined;
  exposurePathname?: undefined;
  isPending: true;
  isVariantSelected?: undefined;
  menuVariantId?: undefined;
  variantName?: undefined;
}

interface IUseMenuVariantOptions {
  isEnabled?: boolean;
}

/**
 * Returns the current variant menu under A/B test, if any.
 *
 * Based on the Menu A/B experiments setup on Amplitude, we could
 * get the variant menu that's under testing from Sanity. If no
 * variant menu matches the current experiment `variantName`,
 * it returns undefined.
 *
 * @param featureMenu The Sanity document with the menu config
 * @param options Controls whether legacy variant evaluation is active
 */
export const useMenuVariant = (
  featureMenu: IFeatureMenuQuery['FeatureMenu'],
  { isEnabled = true }: IUseMenuVariantOptions = {}
): IMenuVariant | IPendingMenuVariant | undefined => {
  const evaluatedMenuExperimentVariant = useMenuExperimentVariant({ isEnabled });
  const isPending = evaluatedMenuExperimentVariant?.isPending === true;
  const menuExperimentVariant =
    isEnabled && !isPending ? evaluatedMenuExperimentVariant : undefined;
  const variantNames =
    menuExperimentVariant?.variantNames ??
    (menuExperimentVariant?.variantName ? [menuExperimentVariant.variantName] : []);

  const sanityMenuVariants = featureMenu?.variants;
  const variantName = variantNames.find(candidateName =>
    sanityMenuVariants?.some(menuVariant => menuVariant?.key?.current === candidateName)
  );
  const foundSanityMenuVariant = sanityMenuVariants?.find(
    menuVariant => menuVariant?.key?.current === variantName
  );

  const menuVariantId = foundSanityMenuVariant?.menuVariantReference?._id;
  const isControlVariant = variantNames.length === 0;
  const isVariantSelected = Boolean(
    menuExperimentVariant && (menuVariantId || (isControlVariant && featureMenu?.defaultMenu?._id))
  );

  if (isPending) {
    return { isPending: true };
  }

  if (!menuExperimentVariant) {
    return;
  }

  return {
    exposureFlagKey: menuExperimentVariant.exposureFlagKey,
    exposurePathname: menuExperimentVariant.exposurePathname,
    isVariantSelected,
    ...(menuVariantId && variantName ? { menuVariantId, variantName } : {}),
  };
};
